import { Alert, Button } from '@design-system';
import { Trans, useTranslation } from 'next-i18next';
import { useState } from 'react';

import {
  OrderReviewSummary,
  OrderReviewSummaryProps,
} from '../OrderReview/OrderReviewSummary';

export type ReviewLayoutProps = Pick<
  OrderReviewSummaryProps,
  | 'cardHold'
  | 'currencyCode'
  | 'framingSelections'
  | 'freeShipping'
  | 'isEstimate'
  | 'maxOrderValue'
  | 'maxQuantity'
  | 'maxQuantityNote'
  | 'onAddFrame'
  | 'orderValue'
  | 'orderedItems'
  | 'shippingCost'
  | 'showPreference'
  | 'showPreview'
  | 'taxRate'
  | 'taxValue'
> & {
  title: string;
  disclaimer: string;
  error?: string;
  errorAction?: { label: string; onClick: () => void };
  submitLabel: string;
  resaleAgreementHref: string;
  isSubmitting?: boolean;
  onSubmit: () => void;
};

export const ReviewLayout = ({
  cardHold,
  currencyCode,
  disclaimer,
  error,
  errorAction,
  framingSelections,
  freeShipping,
  isEstimate,
  isSubmitting,
  maxOrderValue,
  maxQuantity,
  maxQuantityNote,
  onAddFrame,
  onSubmit,
  orderValue,
  orderedItems,
  resaleAgreementHref,
  shippingCost,
  showPreference,
  showPreview,
  submitLabel,
  taxRate,
  taxValue,
  title,
}: ReviewLayoutProps) => {
  const { t } = useTranslation('checkout');
  const [openSummaryIds, setOpenSummaryIds] = useState<Record<string, boolean>>(
    {},
  );

  const toggleSummary = (itemId: string) =>
    setOpenSummaryIds((prev) => ({ ...prev, [itemId]: !prev[itemId] }));

  return (
    <section className="flex flex-col gap-y-3">
      <h2 className="mb-9 text-center text-md font-bold lg:text-lg">{title}</h2>

      <OrderReviewSummary
        cardHold={cardHold}
        currencyCode={currencyCode}
        framingSelections={framingSelections}
        isEstimate={isEstimate}
        freeShipping={freeShipping}
        maxOrderValue={maxOrderValue}
        maxQuantity={maxQuantity}
        maxQuantityNote={maxQuantityNote}
        onAddFrame={onAddFrame}
        onToggleSummary={toggleSummary}
        openSummaryIds={openSummaryIds}
        orderValue={orderValue}
        orderedItems={orderedItems}
        shippingCost={shippingCost}
        showPreference={showPreference}
        showPreview={showPreview}
        taxRate={taxRate}
        taxValue={taxValue}
      />

      {error && (
        <div className="mt-9">
          <Alert status="error">
            {error}
            {errorAction && (
              <>
                {' '}
                <button
                  className="hyperlink"
                  onClick={errorAction.onClick}
                  type="button"
                >
                  {errorAction.label}
                </button>
              </>
            )}
          </Alert>
        </div>
      )}

      <div className={error ? 'mt-3' : 'mt-9'}>
        <Button
          fullWidth
          label={submitLabel}
          loading={isSubmitting}
          onClick={onSubmit}
          variant="primary"
        />
      </div>

      <p className="text-center text-xs text-neutral-4">
        {disclaimer}
        <br />
        <br />
        <Trans
          components={{
            faq: (
              <a
                className="hyperlink"
                href="https://avantarte.com/support/faq"
                rel="noreferrer"
                target="_blank"
              />
            ),
            resale: (
              <a
                className="hyperlink"
                href={resaleAgreementHref}
                rel="noreferrer"
                target="_blank"
              />
            ),
            terms: (
              <a
                className="hyperlink"
                href="https://avantarte.com/support/terms-and-conditions#draws"
                rel="noreferrer"
                target="_blank"
              />
            ),
          }}
          i18nKey="review.legal"
          ns="checkout"
          t={t}
        />
      </p>
    </section>
  );
};
